import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./supabase.ts";

interface AuthContextValue {
  session: Session | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const DEV_BYPASS_USER_ID = import.meta.env.VITE_DEV_BYPASS_USER_ID as string | undefined;

// Local-only escape hatch: any localhost/127.0.0.1 request is treated as
// authenticated with a synthetic dev user. Lets the help-videos Playwright
// recorder drive the dashboard without going through Google OAuth.
// Production hostnames (app.zedule.app, *.pages.dev) never trigger this.
function isLocalDev(): boolean {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return h === "localhost" || h === "127.0.0.1" || h === "0.0.0.0";
}

// Fake Supabase session used only in local dev bypass mode.
function createMockSession(userId: string): Session {
  return {
    access_token: "dev-bypass",
    refresh_token: "dev-bypass",
    expires_in: 3600,
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    token_type: "bearer",
    user: {
      id: userId,
      email: "dev@example.com",
      app_metadata: {},
      user_metadata: { full_name: "Dev User" },
      aud: "authenticated",
      created_at: new Date().toISOString(),
    } as Session["user"],
  } as Session;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (DEV_BYPASS_USER_ID) {
      // Dev mode: skip Supabase, use mock session.
      setSession(createMockSession(DEV_BYPASS_USER_ID));
      setLoading(false);
      return;
    }

    // Localhost-only escape hatch: if there's no real Supabase session in
    // storage, use a mock so the recorder can drive the dashboard without
    // OAuth. If a real JWT IS present (typical when capturing a session
    // for replay), let Supabase use it normally — that way API requests go
    // out with a valid Bearer token the gateway accepts.
    if (isLocalDev()) {
      const sbKey = Object.keys(localStorage).find((k) => k.startsWith("sb-") && k.endsWith("-auth-token"));
      const hasRealSession = sbKey && (() => {
        try {
          const parsed = JSON.parse(localStorage.getItem(sbKey) ?? "");
          return typeof parsed?.access_token === "string" && parsed.access_token.length > 50;
        } catch {
          return false;
        }
      })();
      if (!hasRealSession) {
        setSession(createMockSession("00000000-0000-0000-0000-000000000001"));
        setLoading(false);
        return;
      }
      // else: fall through to the real Supabase auth flow below.
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
        // Always show Google's account chooser instead of silently reusing the
        // single signed-in account (the default on mobile picks the first one).
        queryParams: {
          prompt: "select_account",
        },
      },
    });
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    // Drop cached authed API responses so a signed-out user can't see the
    // prior session's data offline (Workbox "zedule-api" NetworkFirst cache).
    if ("caches" in window) void caches.delete("zedule-api");
  };

  return (
    <AuthContext.Provider value={{ session, loading, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
